import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Tooltip,
  GridLegacy as Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { CorporateFare } from '@mui/icons-material';
import { Page, Row } from '../util/Page';
import { createBuildingActionCreator } from '../../redux/actions/buildings';
import {
  associateBuildingsAction,
  createDeploymentActionCreator,
  upsertDeploymentConfigAction,
} from '../../redux/actions/deployments';
import UnitsLoader from '../loaders/UnitsLoader';
import BuildingsLoader from '../loaders/BuildingsLoader';
import { capitalize } from '../../utils/strings';

import './data-request.css';

const ALPHA_NUMERIC_DASH_REGEX = /^[a-zA-Z0-9- ]+$/;

/* eslint-disable jsx-a11y/label-has-associated-control */
/**
 * A component with internal state which manages a building data request form
 */
const NewBuilding = ({
  orgId,
  orgShortName,
  extantBuildingNames,
  units,
  createBuilding,
  createDeployment,
  linkDeployment,
  updateDeploymentConfig,
}) => {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [changes, setChanges] = useState({});
  const [newBuilding, setNewBuilding] = useState();
  const [newDeployment, setNewDeployment] = useState();
  const [createComplete, setCreateComplete] = useState(false);

  const getField = (key, defaultVal = '') => {
    if (changes[key] != null) return changes[key];
    return defaultVal;
  };
  const setField = (key, value) => setChanges({ ...changes, [key]: value });
  // Autocomplete has 2 params, others have 1
  const change = (key) => (e, newValue) => setField(key, newValue ?? e.target.value);

  const defaultUnit = units.filter((u) => u.name_abbr === 'ft2')?.[0];

  const name = getField('name');
  const nameInvalid = !ALPHA_NUMERIC_DASH_REGEX.test(name);
  const hasAddress = getField('address');
  const area = getField('building-area');
  const areaInvalid = area && !Number(area);
  const nameConflict = extantBuildingNames.includes(name);
  const canSubmit = name && hasAddress && !nameConflict && !areaInvalid && !nameInvalid;
  let submitError = '';
  if (nameConflict) {
    submitError = 'building name already exists';
  }
  if (nameInvalid) {
    if (submitError) submitError += '; ';
    submitError += 'building name must only contains letters, numbers, spaces, and dashes';
  }
  if (areaInvalid) {
    if (submitError) submitError += '; ';
    submitError += 'building area must be a number';
  }
  if (!name || !hasAddress) {
    if (submitError) submitError += '; ';
    submitError += 'please complete these required fields: ';
    let comma = false;
    if (!name) {
      if (comma) submitError += ', ';
      submitError += 'building name';
      comma = true;
    }
    if (!hasAddress) {
      if (comma) submitError += ', ';
      submitError += 'address';
      comma = true;
    }
  }

  function buildSite(buildingName) {
    return [orgShortName.toLowerCase(), buildingName.replace(/[ ]/g, '-')].join('/');
  }

  // Map our internal state to a DataRequest object
  const buildReq = () => ({
    orgId,
    name: getField('name').trim(),
    address: getField('address'),
    purpose: getField('purpose', null),
    area: getField('building-area', null),
    areaUnitId: getField('building-area-unit', defaultUnit)?.id,
  });

  const onSubmit = () => {
    setDialogOpen(true);
    return (
      createBuilding(buildReq())
        .then((building) => {
          setNewBuilding(building);
          return createDeployment(buildSite(building.name), building.name, orgId).then(
            (deployment) => {
              setNewDeployment(deployment.id);
              linkDeployment(deployment.site, building.id);
              return updateDeploymentConfig(deployment.site, 47808);
            }
          );
        })
        .then((result) => {
          setCreateComplete(true);
          return result;
        })
        // eslint-disable-next-line no-console
        .catch((e) => console.log(e))
    );
  };

  const containerSpacing = 6;
  const leftItemXs = 3;
  const rightItemXs = 9;
  let dialogContents;
  if (!newBuilding) {
    dialogContents = 'Preparing new building...';
  } else if (!newDeployment) {
    dialogContents = 'New building created, preparing new deployment...';
  } else if (!createComplete) {
    dialogContents = 'New building created, new deployment created, finishing setup...';
  } else {
    dialogContents = 'New building created, new deployment created, setup complete';
  }
  return (
    <>
      <UnitsLoader />
      <BuildingsLoader />
      <Dialog open={dialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle>Create Building & Deployment</DialogTitle>
        <DialogContent>
          <Typography>{dialogContents}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            disabled={!createComplete}
            onClick={() => {
              navigate(
                `/deployments?expanded=["${newDeployment}"]&filterBy={"buildings"%3A["${newBuilding?.name}"]}`
              );
            }}
            color="success"
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
      <Page name="new-data-request">
        <Row>
          <Grid container spacing={containerSpacing}>
            <Grid item>
              We are excited to help get your project launched! Complete the form to create your new
              building and deployment.
              <br />
              <br />
              Free trials are limited to one building. To launch additional buildings, please
              contact sales@onboarddata.io with your billing information, and our team will assist
              you. Launching additional buildings without payment will result in deactivation.
            </Grid>
          </Grid>
        </Row>
        <>
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                <label className="required" htmlFor="building-name">
                  Building Name
                </label>
              </Grid>
              <Grid item xs={rightItemXs}>
                <TextField
                  id="building-name"
                  required
                  error={!getField('name') || nameConflict}
                  placeholder="Baker Street Lab"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={getField('name')}
                  onChange={change('name')}
                  onKeyDown={(event) => {
                    if (!ALPHA_NUMERIC_DASH_REGEX.test(event.key)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Row>
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                <label htmlFor="building-purpose">Building Purpose</label>
              </Grid>
              <Grid item xs={rightItemXs}>
                <TextField
                  id="building-purpose"
                  placeholder="Office, Stadium, Hospital, Batcave"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={getField('purpose')}
                  onChange={change('purpose')}
                />
              </Grid>
            </Grid>
          </Row>
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                <label className="required" htmlFor="building-address">
                  Building Address
                </label>
              </Grid>
              <Grid item xs={rightItemXs}>
                <TextField
                  id="building-address"
                  required
                  error={!getField('address')}
                  placeholder="221B Baker Street, London, England"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={getField('address')}
                  onChange={change('address')}
                />
              </Grid>
            </Grid>
          </Row>
          <Row>
            <Grid container spacing={containerSpacing}>
              <Grid item xs={leftItemXs}>
                <label htmlFor="building-area">Building Area</label>
              </Grid>
              <Grid item xs={rightItemXs}>
                <TextField
                  id="building-area"
                  error={areaInvalid}
                  placeholder="250000"
                  variant="outlined"
                  size="small"
                  value={getField('building-area')}
                  onChange={change('building-area')}
                />
                <Autocomplete
                  key="building-area-unit"
                  options={units}
                  style={{ display: 'inline-flex', width: '250px' }}
                  getOptionKey={(unit) => unit?.id ?? -1}
                  getOptionLabel={(unit) => unit?.name_long ?? ''}
                  value={getField('building-area-unit', defaultUnit)}
                  onChange={change('building-area-unit')}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  renderInput={(params) => <TextField label="Select" {...params} />}
                />
              </Grid>
            </Grid>
          </Row>
        </>
        <Row>
          <Tooltip title={canSubmit ? '' : capitalize(submitError)}>
            <span>
              <Button
                disabled={!canSubmit}
                onClick={onSubmit}
                variant="contained"
                endIcon={<CorporateFare />}
              >
                Create Building
              </Button>
            </span>
          </Tooltip>
        </Row>
      </Page>
    </>
  );
};

function mapStateToProps(state) {
  const {
    entities: {
      buildings: { byId },
    },
    user: { orgId, orgShortName },
    entities: {
      units: { all },
    },
  } = state;
  return {
    orgId,
    orgShortName,
    units: all
      .filter((u) => u.unit_type === 'http://qudt.org/vocab/quantitykind/Area')
      .filter((u) => u.name_abbr === 'ft2' || u.name_abbr === 'm2'),
    extantBuildingNames: Object.values(byId)
      .filter((b) => b.orgId === orgId)
      .map(({ name }) => name),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    createBuilding: (req) => dispatch(createBuildingActionCreator(req)),
    createDeployment: (site, name, orgId) =>
      dispatch(createDeploymentActionCreator(site, name, orgId)),
    linkDeployment: (site, bid) => dispatch(associateBuildingsAction(site, [bid])),
    updateDeploymentConfig: (site, port) =>
      dispatch(
        upsertDeploymentConfigAction(site, { port, network: '0.0.0.0/32', poll_freq_secs: 60 })
      ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(NewBuilding);
